import { saveLanguage } from './client'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import Icon, { Flag } from '../components/Icon'
import { get, post } from '../lib/api'
import { getLocale, setLocale, t, type Locale } from '../lib/i18n'
import { tw } from './strings'
import { setSoundOn, soundOn } from './feel'

interface Note {
  id: string
  title: string
  body: string
  target: string | null
  read_at: number | null
  created_at: number
}

/**
 * The app bar: the mark, who is here, and what is waiting.
 *
 * ── The mark, not the launcher icon ────────────────────────────────────────
 *
 * `/mark.webp` is the bare W on transparency. The rounded teal tile is the
 * LAUNCHER icon and carries its own background because a home screen has
 * nothing behind it; dropping it into a header puts a second box inside the
 * page's own surface. The white half of the mark is swapped for teal in the
 * same generator, because white is invisible on cream.
 *
 * ── Three flags, and one of them is a joke that earns its place ────────────
 *
 * EN and ES switch the language. The third is Luxembourg — red, white, light
 * blue — and it opens the admin console. It is not a language and it does not
 * claim to be: it is a control that only appears for accounts that hold
 * `admin.access`, in a row where a third small circle costs nothing and a
 * labelled "Admin" button costs the greeting its space. Nobody who should not
 * see it can see it.
 */
export default function Header({
  appName,
  greeting,
  canAdmin,
  adminOpen,
  onToggleAdmin,
  nav,
  badge,
}: {
  appName: string
  /** "Good morning, Marisol" — absent when signed out. */
  greeting?: { hello: string; name: string }
  canAdmin: boolean
  adminOpen: boolean
  onToggleAdmin: () => void
  /** The desktop top navigation, beside the mark. Absent on a phone and in the app. */
  nav?: ReactNode
  /** A small label by the mark, e.g. "Beta preview". */
  badge?: string
}) {
  const [notes, setNotes] = useState<Note[] | null>(null)
  const [open, setOpen] = useState(false)
  const [sound, setSound] = useState(soundOn)
  const locale = getLocale()

  /*
   * Changing locale RELOADS, the same as core's LanguageToggle.
   *
   * `tw()` and `t()` read the locale when they are called, so a React state
   * change would re-render this component and leave every string already
   * rendered elsewhere in the old language. A reload is honest and costs
   * nothing on a bundle this size; it is also what the control this replaces
   * already did, so the behaviour is unchanged.
   */
  const pick = (next: Locale) => {
    if (next === locale) return
    setLocale(next)
    void saveLanguage(next)
    window.location.reload()
  }

  const load = useCallback(async () => {
    try {
      setNotes((await get<{ notifications: Note[] }>('/api/notifications')).notifications)
    } catch {
      setNotes([])
    }
  }, [])

  // Only when there is somebody to notify.
  useEffect(() => { if (greeting) void load() }, [greeting, load])

  const unread = (notes ?? []).filter((n) => !n.read_at).length

  async function openPanel() {
    const next = !open
    setOpen(next)
    if (!next || !unread) return
    /*
     * Opening the panel marks everything in it read.
     *
     * A per-item "mark read" is a button people do not press, and then the dot
     * lies. Reading is what marks it read, the same rule the mailbox uses.
     */
    await Promise.all((notes ?? []).filter((n) => !n.read_at)
      .map((n) => post(`/api/notifications/${n.id}/read`).catch(() => {})))
    await load()
  }

  return (
    <header className="w-bar">
      <a className="w-lockup" href="/" aria-label={appName}>
        <img src="/mark.webp" alt="" width={44} height={31} />
        <span className="w-lockup-words">Vieques<br />Worx</span>
      </a>
      {badge && <span className="w-bar-badge">{badge}</span>}
      {nav}

      {greeting && (
        <p className="w-hello">
          {greeting.hello}, <strong>{greeting.name}</strong>
        </p>
      )}

      <div className="w-bar-end">
        <div className="w-flags" role="group" aria-label="Language / Idioma">
          <button
            className="w-flag"
            aria-pressed={locale === 'en'}
            aria-label="English"
            onClick={() => pick('en')}
          >
            <Flag locale="en" size={22} />
          </button>
          <button
            className="w-flag"
            aria-pressed={locale === 'es-PR'}
            aria-label="Español"
            onClick={() => pick('es-PR')}
          >
            <Flag locale="es-PR" size={22} />
          </button>
          {canAdmin && (
            <button
              className="w-flag"
              aria-pressed={adminOpen}
              aria-label={t('admin.title')}
              title={t('admin.title')}
              onClick={onToggleAdmin}
            >
              <LuxembourgFlag size={22} />
            </button>
          )}
        </div>

        {/* Beside the bell, for both sides and signed out too: whether a phone
            makes noise is about where it is, not who is holding it. */}
        <button
          className="w-bell"
          aria-pressed={sound}
          aria-label={sound ? tw('w.sound.on') : tw('w.sound.off')}
          title={sound ? tw('w.sound.on') : tw('w.sound.off')}
          onClick={() => { setSoundOn(!sound); setSound(!sound) }}
        >
          <Icon name={sound ? 'sound' : 'mute'} size={22} />
        </button>

        {greeting && (
          <button
            className="w-bell"
            aria-label={tw('w.bell.label')}
            aria-expanded={open}
            onClick={() => void openPanel()}
          >
            <Icon name="bell" size={24} />
            {/* A dot, not a count. The number of unread notifications is not a
                quantity anybody acts on differently at 3 than at 12. */}
            {unread > 0 && <span className="w-bell-dot" aria-hidden="true" />}
            {unread > 0 && <span className="w-sr">{unread}</span>}
          </button>
        )}
      </div>

      {open && (
        <div className="w-notes">
          {!notes?.length && <p className="muted w-empty">{t('notify.empty')}</p>}
          <ul>
            {(notes ?? []).map((n) => (
              <li key={n.id} className={n.read_at ? undefined : 'unread'}>
                <strong>{n.title}</strong>
                <span className="muted small">{n.body}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  )
}

/**
 * Luxembourg, in the same circular style as the two language flags.
 *
 * Red, white and LIGHT blue — the light blue is what distinguishes it from the
 * Netherlands, and getting it wrong would make this a Dutch flag opening an
 * admin console, which is a different joke.
 */
function LuxembourgFlag({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="flag">
      <defs><clipPath id="lu-c"><circle cx="12" cy="12" r="11" /></clipPath></defs>
      <g clipPath="url(#lu-c)">
        <rect x="0" y="1" width="24" height="7.33" fill="#ED2939" />
        <rect x="0" y="8.33" width="24" height="7.33" fill="#FFFFFF" />
        <rect x="0" y="15.66" width="24" height="7.34" fill="#00A1DE" />
      </g>
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeOpacity=".18" />
    </svg>
  )
}
